import _sortBy from 'lodash/sortBy'
import _uniqBy from 'lodash/uniqBy'
import _uniq from 'lodash/uniq'
import _map from 'lodash/map'
import _flatten from 'lodash/flatten'
import countryList from 'country-list'
import escapeStringRegexp from 'escape-string-regexp'

const createOpportunityFilterModule = () => ({
  namespaced: true,
  state: {
    activeTypeSlug: '',
    activeCountryCode: '',
    activeCity: '',
    activeCompanySlug: '',
    activeDomainSlug: '',
    activeSkill: '',
    activeLevelSlug: '',
    skillSearch: '',
    viewSkillSearchResult: false
  },
  actions: {
    setActiveCompanyBySlug({ state }, companySlug) {
      state.activeCompanySlug = companySlug
    },
    setActiveTypeBySlug({ state }, typeSlug) {
      state.activeTypeSlug = typeSlug
    },
    setActiveCountryByCode({ state }, code) {
      state.activeCountryCode = code
    },
    setActiveDomainBySlug({ state }, domainSlug) {
      state.activeDomainSlug = domainSlug
    },
    setActiveLevelBySlug({ state }, levelSlug) {
      state.activeLevelSlug = levelSlug
    },
    loadFilters({ dispatch, state, rootState }) {
      if (rootState.route.query.type) {
        const typeSlug = rootState.route.query.type

        dispatch('setActiveTypeBySlug', typeSlug)
      }
      if (rootState.route.query.company) {
        const companySlug = rootState.route.query.company

        dispatch('setActiveCompanyBySlug', companySlug)
      }
      if (rootState.route.query.domain) {
        const domainSlug = rootState.route.query.domain

        dispatch('setActiveDomainBySlug', domainSlug)
      }
      if (rootState.route.query.country) {
        const countryCode = rootState.route.query.country

        dispatch('setActiveCountryByCode', countryCode)
      }
      if (rootState.route.query.city) {
        state.activeCity = rootState.route.query.city
      }
      if (rootState.route.query.level) {
        const levelSlug = rootState.route.query.level

        dispatch('setActiveLevelBySlug', levelSlug)
      }
      if (rootState.route.query.skill) {
        state.activeSkill = rootState.route.query.skill
      }
    },
    clearFilters({ state }, { domain } = { domain: false }) {
      if (domain) {
        state.activeDomainSlug = ''
      }

      state.activeTypeSlug = ''
      state.activeCompanySlug = ''
      state.activeCountryCode = ''
      state.activeCity = ''
      state.activeLevelSlug = ''
      state.activeSkill = ''
    },
    setSkillSearch({ state }, skillSearch) {
      state.skillSearch = skillSearch
    },
    async hideSkillSearchResult({ state }) {
      await new Promise((resolve) => setTimeout(resolve, 200))
      state.viewSkillSearchResult = false
    },
    setActiveSkill({ state }, skill) {
      state.activeSkill = skill
      state.skillSearch = skill
      state.viewSkillSearchResult = false
    },
    setViewSkillSearchResult({ state }, viewSkillSearchResult) {
      state.viewSkillSearchResult = viewSkillSearchResult
    },
    clearFilter({ state }, filterName) {
      state[filterName] = ''
    },
    clearSkillFilter({ state }) {
      state.activeSkill = ''
      state.skillSearch = ''
    }
  },
  getters: {
    hasFilters: (state) => {
      return Object.entries(state)
        .filter(([key]) => /^active/.test(key))
        .some(([, value]) => value !== '')
    },
    activeTypeSlug: ({ activeTypeSlug }) => activeTypeSlug,
    activeType: ({ activeTypeSlug }, getters) =>
      getters.opportunityTypes.find(
        (c) =>
          c.slug === activeTypeSlug ||
          c.slugPlural === activeTypeSlug ||
          c.slugAlternative === activeTypeSlug ||
          c.slugAlternativePlural === activeTypeSlug
      ),
    activeCountryCode: ({ activeCountryCode }) => activeCountryCode,
    activeCountry: ({ activeCountryCode }, getters) =>
      getters.countries.find((c) => c.code === activeCountryCode),
    activeCity: ({ activeCity }) => activeCity,
    activeCompanySlug: ({ activeCompanySlug }) => activeCompanySlug,
    activeCompany: ({ activeCompanySlug }, getters) =>
      getters.companies.find((c) => c.slug === activeCompanySlug),
    activeDomainSlug: ({ activeDomainSlug }) => activeDomainSlug,
    activeDomain: ({ activeDomainSlug }, getters) =>
      getters.domains.find((d) => d.slug === activeDomainSlug),
    activeSkill: ({ activeSkill }) => activeSkill,
    activeLevelSlug: ({ activeLevelSlug }) => activeLevelSlug,
    activeLevel: ({ activeLevelSlug }, getters) =>
      getters.levels.find((l) => l.slug === activeLevelSlug),
    skillSearch: ({ skillSearch }) => skillSearch,
    viewSkillSearchResult: ({ viewSkillSearchResult }) => viewSkillSearchResult,
    matchedSkills: ({ skillSearch }, getters) => {
      if (skillSearch.length < 1) {
        return []
      }

      const searchRegexp = new RegExp(`^${escapeStringRegexp(skillSearch)}`, 'i')

      return getters.skills.filter((skill) => searchRegexp.test(skill)).slice(0, 6)
    },

    companies(state, getters, rootState, rootGetters) {
      const companies = rootGetters['opportunities/opportunities']
        .filter((o) => o.company)
        .map((o) => o.company)

      return _sortBy(_uniqBy(companies, '_id'), 'name')
    },
    opportunityTypes(state, getters, rootState, rootGetters) {
      const opportunityTypes = rootGetters['opportunities/opportunities'].map(
        (o) => o.opportunityType
      )

      return _sortBy(_uniqBy(opportunityTypes, '_id'), 'title')
    },
    countries(state, getters, rootState, rootGetters) {
      const countryCodes = _uniq(
          rootGetters['opportunities/opportunities'].filter((o) => o.country).map((o) => o.country)
        ),
        countries = _map(countryCodes, (cc) => ({
          code: cc,
          name: countryList.getName(cc)
        }))

      return _sortBy(countries, (c) => c.name)
    },
    cities(state, getters, rootState, rootGetters) {
      let opportunities = rootGetters['opportunities/opportunities'].filter((o) => o.country)

      if (this.activeCountry) {
        opportunities = opportunities.filter((o) => o.country === this.activeCountry.code)
      }

      return _uniq(_map(opportunities, (o) => o.city))
    },
    domains(state, getters, rootState, rootGetters) {
      const domains = rootGetters['opportunities/opportunities']
        .filter((o) => o.domain)
        .map((o) => o.domain)

      return _sortBy(_uniqBy(domains, 'slug'), 'priority')
    },
    skills(state, getters, rootState, rootGetters) {
      const skills = rootGetters['opportunities/opportunities']
        .filter((o) => o.skills)
        .map((o) => o.skills)

      return _sortBy(_uniq(_flatten(skills)))
    },
    levels(state, getters, rootState, rootGetters) {
      const levels = rootGetters['opportunities/opportunities']
        .filter((o) => o.skillLevel)
        .map((o) => o.skillLevel)

      return _sortBy(_uniqBy(levels, 'slug'), 'title')
    }
  }
})

export default createOpportunityFilterModule
